import { useState } from "react";
import { Alert, App, Button, Select, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { MAIL_STATUSES, type MailOutboxDto, type MailStatusValue } from "@audit-plan/shared";
import { ApiError } from "../../api/client";
import { useMailOutbox, useRetryMail } from "../../api/admin";
import { Pill, fmtDT } from "../pills";
import { ResponsiveList } from "../ResponsiveList";
import { Sensitive } from "../Sensitive";

const TONE: Record<MailStatusValue, "ok" | "warn" | "bad" | "mute"> = { sent: "ok", queued: "warn", failed: "bad", skipped: "mute" };

/** Posta in uscita: stato, destinatari, oggetto, errori e «Rimanda» per le mail fallite. */
export function MailOutboxView() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [status, setStatus] = useState<string>();
  const q = useMailOutbox(status);
  const retry = useRetryMail();
  const d = q.data;
  const go = async (m: MailOutboxDto) => {
    try {
      await retry.mutateAsync(m.id);
      void message.success(t("c_retried"));
    } catch (e) {
      void message.error(e instanceof ApiError ? e.message : t("a_loadFail"));
    }
  };
  const actions = (m: MailOutboxDto) => (m.status === "failed" ? <Button size="small" loading={retry.isPending} onClick={() => go(m)}>{t("c_retry")}</Button> : null);
  const statusPill = (m: MailOutboxDto) => <Pill tone={TONE[m.status]}>{t(`c_mailStatus_${m.status}`)}</Pill>;
  const who = (m: MailOutboxDto) => <Sensitive>{m.toName ? `${m.toName} <${m.to}>` : m.to}</Sensitive>;
  return (
    <div>
      <div style={{ padding: "12px 16px", display: "grid", gap: 8 }}>
        <div style={{ fontSize: 13, opacity: 0.75 }}>{t("c_outboxHelp", { provider: d?.provider ?? "…" })}</div>
        {d?.provider === "log" && <Alert type="info" showIcon message={t("c_outboxProviderLog")} />}
        {d && d.provider !== "log" && d.sendMode !== "all" && <Alert type="warning" showIcon message={t(d.sendMode === "off" ? "c_outboxSendOff" : "c_outboxSendTests")} />}
        {!!d?.allowedDomains.length && <Typography.Text type="secondary" style={{ fontSize: 13 }}>{t("c_outboxDomains", { d: d.allowedDomains.join(", ") })}</Typography.Text>}
        <Space wrap>
          <Select
            allowClear
            placeholder={t("c_allStatuses")}
            style={{ width: 200 }}
            value={status}
            onChange={setStatus}
            options={MAIL_STATUSES.map((s) => ({ value: s, label: `${t(`c_mailStatus_${s}`)} (${d?.counts[s] ?? 0})` }))}
          />
        </Space>
      </div>
      <ResponsiveList<MailOutboxDto>
        size="small"
        rowKey="id"
        loading={q.isLoading}
        dataSource={d?.items ?? []}
        pageSize={20}
        emptyText={t("none")}
        tableProps={{ expandable: { rowExpandable: (m) => !!m.text || !!m.lastError, expandedRowRender: (m) => <pre data-sensitive style={{ whiteSpace: "pre-wrap", margin: 0, fontFamily: "inherit" }}>{m.lastError ? `${t("c_error")}: ${m.lastError}\n\n` : ""}{m.text ?? ""}</pre> } }}
        card={{
          code: (m) => fmtDT(m.createdAt),
          title: (m) => m.subject,
          subtitle: who,
          tags: (m) => (
            <>
              {statusPill(m)}
              <Pill tone="info">{m.kind}</Pill>
            </>
          ),
          fields: (m) => [
            [t("c_attempts"), String(m.attempts)],
            ...(m.lastError ? ([[t("c_error"), m.lastError]] as [string, string][]) : []),
          ],
          footer: actions,
        }}
        columns={[
          { title: t("date"), dataIndex: "createdAt", render: (v: string) => fmtDT(v) },
          { title: t("status"), dataIndex: "status", render: (_: unknown, m) => statusPill(m) },
          { title: t("recipients"), render: (_: unknown, m) => who(m) },
          { title: t("subject"), dataIndex: "subject" },
          { title: t("kind"), dataIndex: "kind", render: (v: string) => <Pill tone="info">{v}</Pill> },
          { title: t("c_attempts"), dataIndex: "attempts", align: "right" as const },
          { title: t("c_error"), dataIndex: "lastError", ellipsis: true, render: (v: string | null) => v ?? "" },
          { title: "", align: "right" as const, render: (_: unknown, m) => actions(m) },
        ]}
      />
    </div>
  );
}
